import { useState } from "react";

/**
 * 6-digit OTP input shared by every Phase 12 flow that gates a
 * sensitive write behind a one-time code (phone change x2, email
 * change). Owns nothing but the local digit buffer + error string;
 * the parent owns the changeRequestId and the verify mutation. Errors
 * shown inline in friendly language; the "didn't get it / lost
 * access" slot is opt-in so the parent can route those into
 * cancel/restart flows.
 */
export function OtpVerificationStep(props: {
  /** Title rendered above the input. */
  title: string;
  /** Body line — typically "We sent a code to <masked>." */
  body: React.ReactNode;
  /** Submit handler; throws on failure so we can show its message. */
  onSubmit: (code: string) => Promise<void>;
  /** Optional secondary link (e.g. "I lost access — cancel"). */
  secondaryAction?: { label: string; onClick: () => void };
  /** Disable input/submit while a parent mutation is settling. */
  busy?: boolean;
  /** Pre-existing error from the parent (e.g. RATE_LIMITED). */
  initialError?: string | null;
  /** ISO timestamp at which the code expires; renders a hint. */
  expiresAt?: string;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(props.initialError ?? null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmit(code);
    } catch (err) {
      const e = err as { status?: number; apiError?: { code?: string; message?: string } };
      setCode("");
      if (e.apiError?.code === "OTP_INVALID") {
        setError("That code didn't match. Try again.");
      } else if (e.apiError?.code === "OTP_EXPIRED") {
        setError("That code expired. Cancel and start over.");
      } else if (e.status === 429) {
        setError("Too many tries. Take a short break and try again.");
      } else {
        setError(e.apiError?.message ?? "Couldn't verify the code.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = props.busy || submitting;

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-navy">{props.title}</h3>
      <div className="text-xs text-ink-dark">{props.body}</div>
      <input
        className="input text-center text-xl tracking-[0.4em]"
        value={code}
        onChange={(e) =>
          setCode(e.target.value.replace(/\D+/gu, "").slice(0, 6))
        }
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        disabled={disabled}
        aria-label="6-digit verification code"
        required
      />
      {props.expiresAt && (
        <p className="text-[11px] text-ink-dark/70">
          Code expires {new Date(props.expiresAt).toLocaleTimeString()}.
        </p>
      )}
      {error && (
        <p className="text-red-600 text-sm" role="alert">
          {error}
        </p>
      )}
      <button
        type="submit"
        className="btn-primary w-full"
        disabled={code.length !== 6 || disabled}
      >
        {submitting ? "Verifying…" : "Verify"}
      </button>
      {props.secondaryAction && (
        <button
          type="button"
          onClick={props.secondaryAction.onClick}
          className="text-xs text-ink-dark underline self-center"
          disabled={disabled}
        >
          {props.secondaryAction.label}
        </button>
      )}
    </form>
  );
}
